import React, { useState, useMemo, useRef, useCallback } from 'react';
import style from './ProductRow.module.css';
import P from 'components/P';
import _ from 'lodash';
import closeButton from 'resources/img/closeButton.svg';

const ProductRow = ({
  items,
  eventInfo,
  onQuantityChange,
  onDelete,
  onAddFreeItem,
  isMobile
}) => {
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [localQuantities, setLocalQuantities] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const initialItemsOrder = useRef(
    new Map(items.map((item, index) => [item.id, index]))
  );

  const calculateCurrentUsage = (variant) => {
    return items
      .filter(item => item.variant_id === variant.id)
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  const validateFreeItems = useCallback(async (item, newQuantity) => {
    if (!eventInfo) return true;

    const freeItems = items.filter(i => i.free);
    const totalFreeQuantity = freeItems.reduce((sum, i) => sum + i.quantity, 0);

    if (eventInfo.cross) {
      const maxAllowed = Math.floor(newQuantity / eventInfo.N) * eventInfo.M;
      return totalFreeQuantity <= maxAllowed;
    } else {
      const variantCalc = eventInfo.variantCalculations?.[item.variant_id];
      if (!variantCalc) return true;

      const variantAllowed = Math.floor(newQuantity / eventInfo.N) * eventInfo.M;
      const totalRegularQuantity = items
        .filter(i => !i.free)
        .reduce((sum, i) => sum + (i.id === item.id ? newQuantity : i.quantity), 0);

      const maxTotalAllowed = Math.floor(totalRegularQuantity / eventInfo.N) * eventInfo.M;

      return totalFreeQuantity <= Math.min(variantAllowed, maxTotalAllowed);
    }
  }, [eventInfo, items]);

  // 디바운스된 수량 변경 핸들러
  const debouncedQuantityChange = useCallback(
    _.debounce(async (item, quantity) => {
      setIsValidating(true);
      try {
        await onQuantityChange(item, quantity);
      } catch (error) {
        setLocalQuantities(prev => ({
          ...prev,
          [item.id]: item.quantity
        }));
      } finally {
        setIsValidating(false);
      }
    }, 300),
    [onQuantityChange]
  );

  const getMaxAllowableFreeQuantity = (variant) => {
    if (!eventInfo || !variant) return 0;

    // 1. 현재 regular items의 수량 계산
    const regularItemQuantity = items
      .filter(item => item.variant_id === variant.id && !item.free)
      .reduce((sum, item) => sum + item.quantity, 0);

    // 2. 재고 기반 제한
    const inventoryLimit = variant.inventory_quantity - regularItemQuantity;
    if (inventoryLimit <= 0) return 0;

    // 3. 이벤트 기반 제한
    let eventLimit;
    if (eventInfo.cross) {
      eventLimit = eventInfo.remainingFreeItems;
    } else {
      const variantCalc = eventInfo.variantCalculations?.[variant.id];
      if (!variantCalc) return 0;

      const variantAllowedQuantity = variantCalc.remainingFreeItems;
      const eventRemainingQuantity = eventInfo.maxTotalAllowedFreeItems - eventInfo.currentTotalFreeItems;
      eventLimit = Math.min(variantAllowedQuantity, eventRemainingQuantity);
    }

    // 4. 재고 제한과 이벤트 제한 중 더 작은 값 반환
    return Math.min(inventoryLimit, eventLimit);
  };

  const sortedItems = useMemo(() => {
    const regularItems = items.filter(item => !item.free);
    const freeItems = items.filter(item => item.free);

    const sortedRegular = _.sortBy(regularItems, [
      item => new Date(item.created_at).getTime()
    ]);

    const sortedFree = _.sortBy(freeItems, [
      item => new Date(item.created_at).getTime()
    ]);

    return [...sortedRegular, ...sortedFree];
  }, [items]);

  const {
    totalAmount,
    regularItems,
    freeItems,
    eventTag,
    allowedVariants,
    remainingFreeItems
  } = useMemo(() => {
    const regularItems = items.filter(item => !item.free);
    const freeItems = items.filter(item => item.free);

    const allowedVariants = !eventInfo?.cross
      ? regularItems
        .filter(item => {
          const variantCalc = eventInfo?.variantCalculations?.[item.variant_id];
          return variantCalc && variantCalc.remainingFreeItems > 0;
        })
        .map(item => item.variant_id)
      : null;

    const remainingFreeItems = eventInfo?.cross
      ? eventInfo.remainingFreeItems
      : !eventInfo?.cross
        ? regularItems
          .filter(item => {
            const variantCalc = eventInfo?.variantCalculations?.[item.variant_id];
            return variantCalc && variantCalc.remainingFreeItems > 0;
          })
          .reduce((sum, item) => {
            const variantCalc = eventInfo?.variantCalculations?.[item.variant_id];
            return sum + variantCalc.remainingFreeItems;
          }, 0)
        : 0;

    const totalAmount = items.reduce((sum, item) =>
      sum + (item.free ? 0 : item.unit_price * item.quantity), 0
    );

    return {
      totalAmount,
      regularItems,
      freeItems,
      allowedVariants,
      remainingFreeItems,
      eventTag: eventInfo ? `${eventInfo.N}+${eventInfo.M}` : null
    };
  }, [items, eventInfo]);

  const handleQuantityInputChange = (item, e) => {
    const inputValue = e.target.value;
    if (inputValue === '' || parseFloat(inputValue) < 0) return;

    const value = parseInt(inputValue);
    
    // 현재 regular items의 수량 계산
    const regularItemQuantity = items
      .filter(i => i.variant_id === item.variant_id && !i.free)
      .reduce((sum, i) => sum + i.quantity, 0);

    let maxQuantity;
    if (item.free) {
      // free item의 경우, 재고에서 regular item 수량을 뺀 값과 이벤트 제한 중 작은 값으로 제한
      const inventoryLimit = item.variant.inventory_quantity - regularItemQuantity;
      const eventLimit = getMaxAllowableFreeQuantity(item.variant) + item.quantity;
      maxQuantity = Math.min(inventoryLimit, eventLimit);
    } else {
      maxQuantity = item.variant.inventory_quantity;
    }

    let finalValue;
    if (isNaN(value) || value < 1) {
      finalValue = 1;
    } else if (value > maxQuantity) {
      finalValue = maxQuantity;
    } else {
      finalValue = value;
    }

    // free item은 즉시 반영
    if (item.free) {
      onQuantityChange(item, finalValue);
      return;
    }

    // regular item은 로컬 상태 업데이트 후 디바운스 적용
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: finalValue
    }));

    debouncedQuantityChange(item, finalValue);
  };

  const handleQuantityInputBlur = (item, e) => {
    const value = parseInt(e.target.value) || 0;
    if (value < 1) {
      onQuantityChange(item, 1);
    }
  };

  const renderVariantItem = (item) => {
    const maxQuantity = item.free ?
      (getMaxAllowableFreeQuantity(item.variant) + item.quantity) :
      item.variant.inventory_quantity;

    const displayQuantity = item.free ?
      item.quantity :
      (localQuantities[item.id] !== undefined ? localQuantities[item.id] : item.quantity);

    return (
      <div key={item.id} className={style.variantItem}>
        <div className={style.variantDescription}>
          {item.description}
          {item.free && <p className={style.freeTag}>{isMobile ? "free" : "무료증정"}</p>}
        </div>
        <div className={style.quantityControl}>
          <div
            onClick={() => {
              if (!isValidating && displayQuantity > 1) {
                const newQuantity = displayQuantity - 1;
                if (item.free) {
                  onQuantityChange(item, newQuantity);
                } else {
                  setLocalQuantities(prev => ({
                    ...prev,
                    [item.id]: newQuantity
                  }));
                  debouncedQuantityChange(item, newQuantity);
                }
              }
            }}
            className={`${style.quantityButton} ${displayQuantity <= 1 || isValidating ? style.disabled : ''}`}
          >
            -
          </div>
          <div className={style.quantity}>
            <input
              type="number"
              value={displayQuantity}
              onChange={(e) => !isValidating && handleQuantityInputChange(item, e)}
              onBlur={(e) => handleQuantityInputBlur(item, e)}
              className={`${style.quantityInput} ${isValidating ? style.validating : ''}`}
              disabled={isValidating}
              min={1}
              max={maxQuantity}
              style={{
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 'inherit',
                width: "fit-content",
                maxWidth: "45px",
                textAlign: "center"
              }}
            />
          </div>
          <div
            onClick={() => {
              if (!isValidating && displayQuantity < maxQuantity) {
                const newQuantity = displayQuantity + 1;
                if (item.free) {
                  onQuantityChange(item, newQuantity);
                } else {
                  setLocalQuantities(prev => ({
                    ...prev,
                    [item.id]: newQuantity
                  }));
                  debouncedQuantityChange(item, newQuantity);
                }
              }
            }}
            className={`${style.quantityButton} ${displayQuantity >= maxQuantity || isValidating ? style.disabled : ''}`}
          >
            +
          </div>
        </div>
        <P size={isMobile ? 14 : 16} weight="bold">
          {item.free ? '무료' : ` ${(item.unit_price * displayQuantity).toLocaleString()}`}
        </P>
        <img
          src={closeButton}
          onClick={() => !isValidating && onDelete(item)}
          className={style.deleteButton}
          alt="delete"
        />
      </div>
    );
  };

  const firstItem = items[0];
  const canAddFreeItems = eventInfo && (
    eventInfo.cross
      ? remainingFreeItems > 0
      : allowedVariants?.length > 0
  );

  const renderVariantsList = () => (
    <div className={style.variantsList}>
      {sortedItems.map(renderVariantItem)}
      {canAddFreeItems && (
        <>
          <button
            className={`${style.addEventItemButton} ${showEventDropdown ? style.open : ''}`}
            onClick={() => setShowEventDropdown(!showEventDropdown)}
          >
            <span>
              사은품 선택
              {eventInfo.cross
                ? ` (${remainingFreeItems}개 가능)`
                : ' (비교차)'}
            </span>
            <svg width="18" height="14" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 7L0.602885 0.25L8.39711 0.250001L4.5 7Z" fill="#737373" />
            </svg>
          </button>
          {showEventDropdown && (
            <div className={style.eventDropdown}>
              {(() => {
                const currentProductId = firstItem.variant?.product?.id;
                const currentProduct = eventInfo.products.find(p => p.id === currentProductId);

                if (!currentProduct) return null;

                if (eventInfo.cross) {
                  return currentProduct.variants
                    .filter(variant => {
                      // 현재 사용중인 수량 계산
                      const currentUsage = calculateCurrentUsage(variant);
                      // 실제 남은 재고 계산
                      const remainingInventory = variant.inventory_quantity - currentUsage;
                      
                      // 재고가 있고, 이벤트로 추가할 수 있는 수량이 있는 경우만 표시
                      return remainingInventory > 0 && remainingFreeItems > 0;
                    })
                    .map(variant => {
                      // 현재 사용중인 수량
                      const currentUsage = calculateCurrentUsage(variant);
                      // 실제 추가 가능한 수량 (재고 기반)
                      const availableInventory = variant.inventory_quantity - currentUsage;
                      // 이벤트와 재고 중 더 작은 값을 사용
                      const maxPossibleQuantity = Math.min(availableInventory, remainingFreeItems);

                      return (
                        <div
                          key={variant.id}
                          className={style.eventDropdownItem}
                          onClick={() => {
                            onAddFreeItem(eventInfo, variant, firstItem, 1);
                            setShowEventDropdown(false);
                          }}
                        >
                          {`${currentProduct.title} - ${variant.title} `}
                          {`(${maxPossibleQuantity}개 가능)`}
                        </div>
                      );
                    });
                }

                return currentProduct.variants
                  .filter(variant => {
                    // 비교차 이벤트의 경우 variant가 허용된 것인지 확인
                    if (!allowedVariants?.includes(variant.id)) return false;

                    // 현재 사용중인 수량 계산
                    const currentUsage = calculateCurrentUsage(variant);
                    // 실제 남은 재고 계산
                    const remainingInventory = variant.inventory_quantity - currentUsage;

                    // variant별 이벤트 계산 정보
                    const variantCalc = eventInfo.variantCalculations?.[variant.id];
                    if (!variantCalc) return false;

                    // 재고도 있고 이벤트로 추가할 수 있는 수량도 있는 경우만 표시
                    return remainingInventory > 0 && variantCalc.remainingFreeItems > 0;
                  })
                  .map(variant => {
                    // 현재 사용중인 수량
                    const currentUsage = calculateCurrentUsage(variant);
                    // 실제 추가 가능한 수량 (재고 기반)
                    const availableInventory = variant.inventory_quantity - currentUsage;
                    
                    // variant별 이벤트 계산 정보에서 가능한 수량
                    const variantCalc = eventInfo.variantCalculations?.[variant.id];
                    const eventAllowedQuantity = Math.min(
                      variantCalc.remainingFreeItems,
                      eventInfo.maxTotalAllowedFreeItems - eventInfo.currentTotalFreeItems
                    );

                    // 재고와 이벤트 중 더 작은 값을 사용
                    const maxPossibleQuantity = Math.min(availableInventory, eventAllowedQuantity);

                    return (
                      <div
                        key={variant.id}
                        className={style.eventDropdownItem}
                        onClick={() => {
                          onAddFreeItem(eventInfo, variant, firstItem, maxPossibleQuantity);
                          setShowEventDropdown(false);
                        }}
                      >
                        {`${currentProduct.title} - ${variant.title} `}
                        {`(${maxPossibleQuantity}개 가능)`}
                      </div>
                    );
                  });
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderTitleSection = () => (
    <div className={style.titleSection}>
      <div className={style.titleMain}>
        <div className={style.titleInfo}>
          <P size={isMobile ? 16 : 18} weight="bold" className={style.productTitle}>
            {firstItem.title}
          </P>
          {eventTag && (
            <p className={style.eventTag}>
              {eventTag}
              {!eventInfo.cross && " (비교차)"}
              {eventInfo.cross && ` (교차)`}
            </p>
          )}
        </div>
        <div className={style.totalAmount}>
         {totalAmount.toLocaleString()}
        </div>
      </div>
    </div>
  );

  return (
    <div className={style.productRow}>
      {isMobile ? (
        <>
          <div className={style.topSection}>
            <img
              src={firstItem.thumbnail}
              alt={firstItem.title}
              className={style.thumbnail}
            />
            <div className={style.contentArea}>
              {renderTitleSection()}
            </div>
          </div>
          {renderVariantsList()}
        </>
      ) : (
        <>
          <img
            src={firstItem.thumbnail}
            alt={firstItem.title}
            className={style.thumbnail}
          />
          <div className={style.contentArea}>
            {renderTitleSection()}
            {renderVariantsList()}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductRow;